import type { MultiPolygon as GeoJsonMultiPolygon, Polygon as GeoJsonPolygon, Position } from 'geojson';

import type { Bounds, Point } from '~types';

/**
 * Calculate bounding box for a set of coordinate points.
 * @param coords - Array of [lon, lat] points
 * @returns Calculated bounds
 * @throws If the coordinates array is empty
 */
const calculateBounds = (coords: Point[]): Bounds => {
  if (!coords.length) {
    throw new Error('Cannot calculate bounds from empty coordinates');
  }

  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;

  for (const [lon, lat] of coords) {
    minLon = Math.min(minLon, lon);
    maxLon = Math.max(maxLon, lon);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  }

  return {
    minLon,
    maxLon,
    minLat,
    maxLat,
    width: maxLon - minLon,
    height: maxLat - minLat,
  };
};

/**
 * Calculate bounding box from GeoJSON polygon coordinate rings.
 * Flattens all rings (outer + holes) into a single bounds computation.
 * @param rings - GeoJSON polygon coordinate rings (Position[][])
 * @returns Calculated bounds
 */
const calculateBoundsFromRings = (rings: Position[][]): Bounds => {
  const points: Point[] = rings.flatMap((ring) => ring.map((pos) => [pos[0], pos[1]] as Point));
  return calculateBounds(points);
};

/**
 * Merge multiple bounding boxes into a single enclosing bounding box.
 * @param boxes - Array of Bounds to merge
 * @returns A single Bounds enclosing all input bounds
 * @throws If the array is empty
 */
const mergeBounds = (boxes: Bounds[]): Bounds => {
  if (!boxes.length) {
    throw new Error('Cannot merge empty bounds array');
  }

  if (boxes.length === 1) return boxes[0];

  const corners: Point[] = boxes.flatMap((b) => [
    [b.minLon, b.minLat],
    [b.maxLon, b.maxLat],
  ]);

  return calculateBounds(corners);
};

/**
 * Compute geographic bounds from an array of Polygon/MultiPolygon geometries.
 * Iterates all coordinate rings to find the min/max lon/lat extents.
 * @returns Bounds object, or null if no valid coordinates are found
 */
const computeBoundsFromGeometries = (geometries: (GeoJsonPolygon | GeoJsonMultiPolygon)[]): Bounds | null => {
  if (!geometries?.length) return null;

  const allBounds: Bounds[] = [];

  for (const geom of geometries) {
    const rings = geom.type === 'MultiPolygon' ? geom.coordinates.flat() : geom.coordinates;
    if (rings.some((ring) => ring.length > 0)) {
      allBounds.push(calculateBoundsFromRings(rings));
    }
  }

  if (!allBounds.length) return null;

  const merged = mergeBounds(allBounds);
  if (merged.width === 0 && merged.height === 0) return null;

  return merged;
};

/**
 * Expand bounds with a padding factor (fraction of width/height per side).
 * @param bounds - Original bounding box
 * @param padding - Fraction to pad on each side (default: 0.05 = 5%)
 * @returns New bounds expanded by the padding factor
 */
const padBounds = (bounds: Bounds, padding = 0.05): Bounds => {
  const padX = bounds.width * padding;
  const padY = bounds.height * padding;

  const minLon = bounds.minLon - padX;
  const maxLon = bounds.maxLon + padX;
  const minLat = bounds.minLat - padY;
  const maxLat = bounds.maxLat + padY;

  return {
    minLon,
    maxLon,
    minLat,
    maxLat,
    width: maxLon - minLon,
    height: maxLat - minLat,
  };
};

export { calculateBounds, calculateBoundsFromRings, computeBoundsFromGeometries, mergeBounds, padBounds };
